import { Injectable } from '@angular/core';
import { Observable, forkJoin, of, map, catchError } from 'rxjs';
import { IFormTemplate, IFormField, IFormSection, IFormValidationRule, IFormCrossValidation, IDataSourceConfiguration, IDataSource } from '../models/form-template';
import { IDynamicFormConfig, IDynamicFormField, IDynamicFormGroup, IDynamicEditorOptions, IDynamicValidationRule } from '../models/dynamic-form';
import { parseTypedValue, toDynamicParamMapping, buildYearRange } from './dynamic-form-builder.helpers';
import { DynamicFormDataSourceService } from './dynamic-form-data-source.service';
import { UtilitiesService } from '../../shared/components/framework/services/utilities.service';

@Injectable({ providedIn: 'root' })
export class DynamicFormBuilderService {

  constructor(private readonly dataSourceService: DynamicFormDataSourceService) {}

  /** Drops cached data-source responses so the next form open refetches them. */
  clearDataSourceCache(): void {
    this.dataSourceService.clearCache();
  }

  /**
   * Held only for the duration of a single buildDynamicForm() call so that
   * private helpers can resolve data sources without parameter threading.
   */
  private _dataSources: { [key: string]: IDataSource } = {};

  buildDynamicForm(template: IFormTemplate): IDynamicFormConfig {
    const colCount = template.form.defaultColCount;
    const submit = template.form.submitButtonLabel || 'Submit';
    const hasTemplateRequestType = (template.fields ?? []).some(
      (field) => field.isActive && field.fieldKey?.trim().toLowerCase() === 'requesttype'
    );
    // Stash data sources for use by private helpers, then clear when done.
    this._dataSources = template.dataSources ?? {};
    const result = {
      form: {
        colCount
      },
      items: [
        ...(hasTemplateRequestType ? [] : [this.defaultRequestType()]),
        ...this.buildFormItems(template)
      ].sort((a, b) => a.index - b.index).concat([this.buildSubmitButton(colCount, submit)]),
      dataSources: Object.values(template.dataSources ?? {}).map(ds => ({
        ...ds,
        configuration: (typeof ds.configuration === 'string'
          ? JSON.parse(ds.configuration)
          : ds.configuration) as IDataSourceConfiguration
      }))
    };
    this._dataSources = {};
    return result;
  }

  private buildSubmitButton(colSpan: number, label: string): any {
    return {
      colSpan,
      horizontalAlignment: 'center',
      cssClass: 'borrow-submit',
      editorType: 'dxButton',
      editorOptions: { text: label, useSubmitBehavior: true }
    };
  }

  private buildFormItems(template: IFormTemplate): (IDynamicFormField | IDynamicFormGroup)[] {
    return [
      ...template.sections
        .map(section => this.buildFormSection(section, template.fields.filter(f => f.sectionKey === section.sectionKey && f.isActive))),
      ...template.fields
        .filter(f => !f.sectionKey && f.isActive)
        .flatMap(field => this.buildFieldItems(field))
    ];
  }

  private buildFormSection(section: IFormSection, fields: IFormField[]): IDynamicFormGroup {
    return {
      itemType: 'group',
      colCount: section.colCount,
      colSpan: section.colSpan,
      index: section.sectionOrder,
      items: fields
        .sort((a, b) => a.fieldOrder - b.fieldOrder)
        .flatMap(field => this.buildFieldItems(field))
    }
  }

  /** Returns one item for most widgets, but two for custom-validated widgets (widget + proxy). */
  private buildFieldItems(field: IFormField): any[] {
    const item = this.buildFieldItem(field);
    if (field.widgetType === 'file-upload') {
      return [item, this.buildFileUploadProxy(field)];
    }
    if (field.widgetType === 'loan-amount-grid') {
      return [item, this.buildLoanAmountProxy(field)];
    }
    // currency-amount writes directly into requestData; no hidden proxy needed.
    return [item];
  }

  private buildFieldItem(field: IFormField): any {
    switch (field.widgetType) {
      case 'standard':
        return this.buildStandardField(field);

      case 'mandate-selector':
        return this.buildStandardField(field);

      case 'loan-amount-grid':
        return { ...this.buildCustomWidget(field, 'loanAmountGridTemplate'), label: { visible: false } };

      case 'file-upload':
        return this.buildCustomWidget(field, 'fileUploadTemplate');

      case 'display-label':
        return { ...this.buildCustomWidget(field, 'displayLabelTemplate'), label: { visible: false } };

      case 'currency-amount':
        return this.buildCustomWidget(field, 'currencyAmountTemplate');

      default:
        console.warn(`Unknown widget type: ${field.widgetType}`);
        return this.buildStandardField(field);
    }
  }

  /**
   * Generates the hidden proxy item that anchors DevExtreme validation to the
   * file-upload widget. `_minFiles` is stored on the item so the inject helper
   * can build the right closure without access to component state.
   */
  private buildFileUploadProxy(field: IFormField): any {
    const parsedMetadata = field.metadata
      ? (typeof field.metadata === 'string' ? JSON.parse(field.metadata) : field.metadata)
      : null;
    const message = field.isRequired ? parsedMetadata?.config?.validationRules?.message ?? 'At least one file is required' : '';
    const minFiles: number = field.isRequired ? parsedMetadata?.config?.validationRules?.minFiles ?? 1 : 0;
    return {
      dataField: 'fileUploadProxy',
      colSpan: field.colSpan,
      itemType: 'simple',
      index: field.fieldOrder + 0.5,
      label: { visible: false },
      editorType: 'dxTextBox',
      editorOptions: { visible: false, value: '0' },
      _minFiles: minFiles,
      validationRules: [
        { type: 'custom', message, reevaluate: true }
      ]
    };
  }

  /**
   * Generates the hidden proxy item that anchors DevExtreme validation to the
   * loan-amount-grid widget. `_minRows` and `_sourceFieldKey` are stored on the
   * item so the inject helper can build the right closure.
   */
  private buildLoanAmountProxy(field: IFormField): any {
    const parsedMetadata = field.metadata
      ? (typeof field.metadata === 'string' ? JSON.parse(field.metadata) : field.metadata)
      : null;
    const message = parsedMetadata?.config?.validationRules?.message ?? 'At least one loan amount must be entered';
    const minRows: number = parsedMetadata?.config?.validationRules?.minRows ?? 1;
    const maxRows: number | null = parsedMetadata?.config?.validationRules?.maxRows ?? null;
    return {
      dataField: 'loanAmountsProxy',
      colSpan: field.colSpan,
      itemType: 'simple',
      index: field.fieldOrder + 0.5,
      label: { visible: false },
      editorType: 'dxTextBox',
      editorOptions: { visible: false, value: '0' },
      _minRows: minRows,
      _maxRows: maxRows,
      _sourceFieldKey: field.fieldKey,
      validationRules: [
        { type: 'custom', message, reevaluate: true }
      ]
    };
  }

  /**
   * Generates the hidden proxy item that anchors DevExtreme validation to the
   * currency-amount widget. `_amountFieldKey` and `_currencyFieldKey` identify
   * the underlying requestData fields, while `_minAmount` drives validation.
   */
  private buildCurrencyAmountProxy(field: IFormField): any {
    const parsedMetadata = field.metadata
      ? (typeof field.metadata === 'string' ? JSON.parse(field.metadata) : field.metadata)
      : null;
    const currencyFieldKey: string = parsedMetadata?.config?.currencyFieldKey ?? 'currency';
    const minAmount: number = parsedMetadata?.config?.validationRules?.minAmount
      ?? parsedMetadata?.config?.minAmount
      ?? 0;
    const message = field.isRequired
      ? parsedMetadata?.config?.validationRules?.message ?? 'Currency and amount are required'
      : parsedMetadata?.config?.validationRules?.message ?? 'Enter a valid amount';

    return {
      dataField: this.getCurrencyAmountProxyFieldKey(field.fieldKey),
      colSpan: field.colSpan,
      itemType: 'simple',
      index: field.fieldOrder + 0.5,
      label: { visible: false },
      editorType: 'dxTextBox',
      editorOptions: { visible: false, value: '' },
      _proxyType: 'currencyAmount',
      _amountFieldKey: field.fieldKey,
      _currencyFieldKey: currencyFieldKey,
      _minAmount: minAmount,
      _isRequired: field.isRequired,
      validationRules: [
        { type: 'custom', message, reevaluate: true }
      ]
    };
  }

  /**
   * Post-build helper: injects a validationCallback into every fileUploadProxy
   * custom rule. The callback reads `_minFiles` from the item rather than relying
   * on any component-level flag, so validation is fully driven by DB config.
   *
   * @param filesGetter - Arrow returning the current file array (e.g. `() => this.files`)
   */
  injectFileUploadProxyCallbacks(config: IDynamicFormConfig, filesGetter: () => File[]): void {
    const inject = (items: any[]): void => {
      items.forEach(item => {
        if (item.dataField === 'fileUploadProxy') {
          const minFiles: number = item._minFiles ?? 1;
          item.validationRules?.forEach((rule: any) => {
            if (rule.type === 'custom') {
              rule.validationCallback = (e: any) => {
                const isValid = filesGetter().length >= minFiles;
                e.isValid = isValid;
                return isValid;
              };
            }
          });
        }
        if (item.items) inject(item.items);
      });
    };
    inject(config.items as any[]);
  }

  /**
   * Post-build helper: injects a validationCallback into every loanAmountsProxy
   * custom rule. The callback reads `_minRows` and `_sourceFieldKey` from the
   * item so each proxy validates the right field independently.
   *
   * @param rowsGetter - Arrow returning rows for a given fieldKey (e.g. `(k) => this.requestData[k] ?? []`)
   */
  injectLoanAmountProxyCallbacks(config: IDynamicFormConfig, rowsGetter: (fieldKey: string) => any[]): void {
    const inject = (items: any[]): void => {
      items.forEach(item => {
        if (item.dataField === 'loanAmountsProxy') {
          const minRows: number = item._minRows ?? 1;
          const maxRows: number | null = item._maxRows ?? null;
          const sourceKey: string = item._sourceFieldKey;
          item.validationRules?.forEach((rule: any) => {
            if (rule.type === 'custom') {
              rule.validationCallback = (e: any) => {
                const rows = rowsGetter(sourceKey) ?? [];
                const isValid = rows.length >= minRows && (maxRows == null || rows.length <= maxRows);
                e.isValid = isValid;
                return isValid;
              };
            }
          });
        }
        if (item.items) inject(item.items);
      });
    };
    inject(config.items as any[]);
  }

  /**
   * Post-build helper: injects a validationCallback into every currency-amount
   * proxy custom rule so the composite widget can participate in validation.
   */
  injectCurrencyAmountProxyCallbacks(
    config: IDynamicFormConfig,
    valueGetter: (amountFieldKey: string, currencyFieldKey: string) => { amount: any; currency: any }
  ): void {
    const inject = (items: any[]): void => {
      items.forEach(item => {
        if (item._proxyType === 'currencyAmount') {
          const amountFieldKey: string = item._amountFieldKey;
          const currencyFieldKey: string = item._currencyFieldKey;
          const minAmount: number = item._minAmount ?? 0;
          const isRequired: boolean = item._isRequired === true;
          item.validationRules?.forEach((rule: any) => {
            if (rule.type === 'custom') {
              rule.validationCallback = (e: any) => {
                const { amount, currency } = valueGetter(amountFieldKey, currencyFieldKey);
                const numericAmount = Number(amount);
                const hasAmount = amount !== null && amount !== undefined && amount !== '' && Number.isFinite(numericAmount);
                const hasCurrency = typeof currency === 'string' && currency.trim().length > 0;
                const isValid = isRequired
                  ? hasCurrency && hasAmount && numericAmount > minAmount
                  : (!hasAmount && !hasCurrency) || (hasCurrency && hasAmount && numericAmount > minAmount);
                e.isValid = isValid;
                return isValid;
              };
            }
          });
        }
        if (item.items) inject(item.items);
      });
    };
    inject(config.items as any[]);
  }

  private getCurrencyAmountProxyFieldKey(fieldKey: string): string {
    return `${fieldKey}CurrencyAmountProxy`;
  }

  private buildStandardField(field: IFormField): IDynamicFormField {
    const parsedMetadata = field.metadata
      ? (typeof field.metadata === 'string' ? JSON.parse(field.metadata) : field.metadata)
      : null;
    const behaviours = parsedMetadata?.behaviours ?? [];
    const editorOptions = this.parseEditorOptions(field);
    this.applyDataSource(field, editorOptions);

    // Stamp a stable data-testid on the editor root element (via elementAttr)
    // so tests can locate fields by fieldKey without relying on CSS classes.
    (editorOptions as any).elementAttr = {
      ...((editorOptions as any).elementAttr ?? {}),
      'data-testid': `dynamic-form-field-${field.fieldKey}`
    };

    // inlineLabel: true hides the DevExtreme form label and moves the text
    // into editorOptions.text, producing [□] Text rather than Text: [□]
    const useInlineLabel = parsedMetadata?.inlineLabel === true;
    const label = useInlineLabel
      ? { visible: false }
      : { text: field.label };
    if (useInlineLabel && !editorOptions['text']) {
      (editorOptions as any)['text'] = field.label;
    }

    const crossValidations: IFormCrossValidation[] = parsedMetadata?.crossValidation ?? [];
    const item: any = {
      dataField: field.fieldKey,
      editorType: field.inputType,
      label,
      colSpan: field.colSpan,
      cssClass: field.cssClasses,
      index: field.fieldOrder,
      editorOptions,
      validationRules: [
        ...this.parseValidationRules(field.validationRules),
        // Placeholder custom rules for cross-field validation — callbacks are
        // wired at inject time by injectValidationCallbacks().
        ...crossValidations.map(cv => ({ type: 'custom' as const, message: cv.message, reevaluate: true }))
      ],
      behaviours: behaviours.length ? behaviours : undefined
    };

    // Stamp cross-validation metadata so injectValidationCallbacks can
    // locate this item and wire the correct formDataRef-dependent closures.
    if (crossValidations.length) {
      item._crossValidation = crossValidations;
    }

    // For dynamic sources (graphql/api), stamp the key so injectDataSourceItems
    // can populate editorOptions.items after the component fetches the data.
    if (field.dataSourceKey) {
      const ds = this._dataSources[field.dataSourceKey];
      if (ds && (ds.sourceType !== 'static' || field.dataSourceKey === 'mandates')) {
        item._dataSourceKey = field.dataSourceKey;
      }
    }

    return item;
  }

  /**
   * Resolves the data source for a field and applies valueExpr / displayExpr /
   * items directly onto the editorOptions object.  Static sources are fully
   * resolved here; dynamic sources get their lookup options applied but items
   * are left empty until injectDataSourceItems() is called.
   */
  private applyDataSource(field: IFormField, editorOptions: IDynamicEditorOptions): void {
    if (!field.dataSourceKey) return;
    const ds = this._dataSources[field.dataSourceKey];
    if (!ds) return;

    const config: IDataSourceConfiguration = typeof ds.configuration === 'string'
      ? JSON.parse(ds.configuration)
      : ds.configuration;

    // Apply shared lookup presentation options from the data source config.
    if (config.valueExpr) editorOptions.valueExpr = config.valueExpr;
    if (config.displayExprTemplate) {
      editorOptions.displayExpr = this.compileDisplayExprTemplate(config.displayExprTemplate);
    } else if (config.displayExpr) {
      editorOptions.displayExpr = config.displayExpr;
    }
    // Static sources: resolve items immediately — no runtime injection needed.
    if (ds.sourceType === 'static' && config.items && field.dataSourceKey !== 'mandates') {
      editorOptions.items = config.items;
    }
  }

  /**
   * Compiles a display expression template string (e.g. "{name} ({status})")
   * into a function suitable for DevExtreme displayExpr.
   */
  private compileDisplayExprTemplate(template: string): (item: any) => string {
    return (item: any) => {
      if (!item) return '';
      return template.replace(/\{(\w+)\}/g, (_, key) => item[key] ?? '');
    };
  }

  private buildCustomWidget(field: IFormField, templateRef: string): any {
    // metadata may arrive as a JSON string from the API — parse it if needed.
    const parsedMetadata = field.metadata
      ? (typeof field.metadata === 'string' ? JSON.parse(field.metadata) : field.metadata)
      : null;
    return {
      itemType: 'custom',
      name: field.fieldKey,
      colSpan: field.colSpan,
      template: templateRef,
      index: field.fieldOrder,
      label: field.label ? { text: field.label } : undefined,
      metadata: {
        ...(parsedMetadata?.config ?? {}),
        fieldLabel: field.label
      }
    };
  }

  private parseEditorOptions(field: IFormField): IDynamicEditorOptions {
    const parsedDefaultValue = parseTypedValue(field.defaultValue, field.inputType);
    const baseOptions: IDynamicEditorOptions = {
      value: this.resolveDefaultValue(field, parsedDefaultValue),
      placeholder: field.placeholder
      // readOnly is NOT derived from isRequired — required fields are still
      // editable; use validationRules to enforce the constraint instead.
    };

    // metadata arrives from the API as a JSON string — parse it before spreading.
    const parsedMetadata = field.metadata
      ? (typeof field.metadata === 'string' ? JSON.parse(field.metadata) : field.metadata)
      : null;

    const options = { ...baseOptions, ...parsedMetadata?.editorOptions };

    // Rolling year list (e.g. financial year): current year + offset for count + 1 years.
    if (parsedMetadata?.yearRange) {
      const currentYear = new Date().getFullYear();
      options.items = buildYearRange(currentYear, parsedMetadata.yearRange.offset ?? 0, parsedMetadata.yearRange.count ?? 10);
      if (options.value === undefined || options.value === null) {
        options.value = currentYear;
      }
    }

    if (field.inputType === 'dxDateBox' && options.min === 'tomorrow') {
      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);
      options.min = tomorrow;
    }

    // Match legacy UX for select boxes when template placeholder is omitted.
    if (field.inputType === 'dxSelectBox' && !options.placeholder) {
      options.placeholder = 'Select...';
    }

    return options;
  }

  private resolveDefaultValue(
    field: IFormField,
    parsedDefaultValue: boolean | number | Date | string | undefined
  ): boolean | number | Date | string | undefined {
    if (
      field.formType === 'AVIVA_ONBOARDING_TENANCY'
      && field.fieldKey === 'financialYear'
      && field.inputType === 'dxNumberBox'
      && (parsedDefaultValue === undefined || parsedDefaultValue === null)
    ) {
      return new Date().getFullYear();
    }

    return parsedDefaultValue;
  }

  /**
   * Post-build helper: injects onValueChanged handlers for any field that has
   * declarative `behaviours` in its Metadata. The component calls this after
   * buildDynamicForm(), passing its own requestData object so the closures can
   * mutate it directly (same pattern as injectFileUploadProxyCallbacks).
   *
   * @param formDataRef - The component's requestData object (e.g. `this.requestData`)
   * @param formGetter  - Optional arrow returning the live DevExtreme form instance.
   *                      Required for behaviours that need to update editor options
   *                      at runtime (e.g. showInactiveLoansChanged).
   */
  injectBehaviorCallbacks(config: IDynamicFormConfig, formDataRef: any, formGetter?: () => any): void {
    const inject = (items: any[]): void => {
      items.forEach(item => {
        if (item.behaviours?.length) {
          const existing = item.editorOptions ?? {};
          const handlers: Array<(e: any) => void> = [];

          if (typeof existing['onValueChanged'] === 'function') {
            handlers.push(existing['onValueChanged']);
          }

          for (const behaviour of item.behaviours) {
            if (behaviour.event === 'onValueChanged') {
              if (behaviour.action === 'setField') {
                handlers.push((e: any) => {
                  if (this.evalCondition(behaviour.condition, e)) {
                    formDataRef[behaviour.targetField] = behaviour.targetValue;
                  }
                });
              } else if (behaviour.action === 'showInactiveLoansChanged') {
                const targetField: string = behaviour.targetField;
                handlers.push((e: any) => {
                  if (!targetField || !formGetter) return;
                  const formInstance = formGetter();
                  if (!formInstance) return;
                  const editor = formInstance.getEditor(targetField);
                  if (!editor) return;
                  const targetItem = this.findItemByDataField(config.items as any[], targetField);
                  const allItems: any[] = targetItem?._allItems ?? editor.option('items') ?? [];
                  const showInactive: boolean = !!e.value;
                  const filtered = showInactive
                    ? allItems
                    : allItems.filter((i: any) => !this.isItemInactive(i));
                  editor.option('items', filtered);
                });
              } else if (behaviour.action === 'reloadDataSource') {
                const targetField: string = behaviour.targetField;
                const fieldsToClear: string[] = [targetField, ...(behaviour.clearFields ?? [])];
                let requestVersion = 0;
                handlers.push((e: any) => {
                  if (!targetField) return;
                  if (item.dataField) {
                    formDataRef[item.dataField] = e?.value;
                  }
                  const formInstance = formGetter?.();
                  fieldsToClear.forEach(field => {
                    formDataRef[field] = null;
                    formInstance?.updateData(field, null);
                  });
                  const version = ++requestVersion;
                  this.reloadTargetItems(config, targetField, formDataRef, formGetter, () => version === requestVersion);
                });
              } else if (behaviour.action === 'clearLoanAmountRows') {
                const targetField: string = behaviour.targetField;
                handlers.push((e: any) => {
                  if (!targetField) return;

                  // Persist the triggering field value first (e.g. mandate)
                  // so downstream datasource refreshes read the latest context.
                  if (item.dataField) {
                    formDataRef[item.dataField] = e?.value;
                  }

                  formDataRef[targetField] = [];
                  formDataRef.__loanLookupVersion = (formDataRef.__loanLookupVersion ?? 0) + 1;

                  if (formGetter) {
                    const formInstance = formGetter();
                    if (formInstance) {
                      if (item.dataField) {
                        formInstance.updateData(item.dataField, e?.value);
                      }
                      formInstance.updateData(targetField, []);
                      const proxyEditor = formInstance.getEditor('loanAmountsProxy');
                      if (proxyEditor) {
                        proxyEditor.option('value', '0');
                      }
                    }
                  }
                });
              } else if (behaviour.action === 'setFieldVisibility') {
                const targetField: string = behaviour.targetField;
                handlers.push((e: any) => {
                  if (!targetField || !formGetter) return;
                  if (!this.evalCondition(behaviour.condition, e)) return;

                  const formInstance = formGetter();
                  if (!formInstance) return;

                  const isVisible = behaviour.targetValue !== false;
                  formInstance.itemOption(targetField, 'visible', isVisible);
                });
              } else if (behaviour.action === 'populateFromSourceItem') {
                const targetField: string = behaviour.targetField;
                const sourceItemKey: string = behaviour.sourceItemKey ?? '';
                const format: string = behaviour.format ?? '';
                handlers.push((e: any) => {
                  if (!targetField || !sourceItemKey) return;
                  const formInstance = formGetter?.();
                  if (item.dataField) {
                    formDataRef[item.dataField] = e?.value;
                    if (formInstance) {
                      formInstance.updateData(item.dataField, e?.value);
                      formInstance.getEditor(item.dataField)?.option('value', e?.value);
                    }
                  }
                  // item._allItems is populated by injectDataSourceItems after resolveDataSources;
                  // fall back to current editor items if _allItems not yet set.
                  const valueExpr: string = item.editorOptions?.valueExpr ?? 'name';
                  const currentItems = formGetter?.()?.getEditor(item.dataField)?.option('items') ?? [];
                  const candidates: any[] = (item as any)._allItems ?? currentItems;
                  const selected = candidates.find((i: any) => i[valueExpr] === e.value);

                  let formatted = '';
                  if (selected != null && selected[sourceItemKey] != null) {
                    const raw = selected[sourceItemKey];
                    if (format === 'joinComma' && Array.isArray(raw)) {
                      formatted = raw.map((ln: any) =>
                        typeof ln === 'string' ? ln : (ln?.name ?? ln?.toString() ?? String(ln))
                      ).join(', ');
                    } else {
                      formatted = String(raw);
                    }
                  }

                  formDataRef[targetField] = formatted;

                  if (formInstance) {
                    formInstance.updateData(targetField, formatted);
                    formInstance.getEditor(targetField)?.option('value', formatted);
                  }
                });
              }
            }
          }

          if (handlers.length) {
            existing['onValueChanged'] = (e: any) => {
              handlers.forEach((h) => h(e));
            };
          }

          item.editorOptions = existing;
        }
        if (item.items) inject(item.items);
      });
    };
    inject(config.items as any[]);
  }

  /**
   * Post-build helper: wires validationCallback closures for any field that has
   * declarative crossValidation in its Metadata. Call after buildDynamicForm()
   * and before binding the config to the form.
   *
   * @param formDataRef - The component's requestData object (e.g. `this.requestData`)
   */
  injectValidationCallbacks(config: IDynamicFormConfig, formDataRef: any): void {
    const inject = (items: any[]): void => {    
      items.forEach(item => {
        if (item._crossValidation?.length) {
          // Cross-validation placeholder rules are appended after any standard
          // rules, so the index of the first is (total - crossValidation.length).
          const totalRules: number = item.validationRules?.length ?? 0;
          const cvCount: number = item._crossValidation.length;
          const cvStartIndex: number = totalRules - cvCount;
          item._crossValidation.forEach((cv: IFormCrossValidation, i: number) => {
            const rule = item.validationRules?.[cvStartIndex + i];
            if (!rule || rule.type !== 'custom') return;

            if (cv.type === 'atLeastOneRequired') {
              const siblings = cv.fields.filter((f: string) => f !== item.dataField);
              rule.validationCallback = (e: any) => {
                return !!(e.value || siblings.some((f: string) => !!formDataRef[f]));
              };
            }
          });
        }
        if (item.items) inject(item.items);
      });
    };
    inject(config.items as any[]);
  }

  /** Recursively finds the first item matching the given dataField. */
  private findItemByDataField(items: any[], dataField: string): any {
    for (const item of items) {
      if (item.dataField === dataField) return item;
      if (item.items) {
        const found = this.findItemByDataField(item.items, dataField);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Returns true when a data-source item should be considered inactive.
   * Handles both API conventions:
   *  - isActive: boolean  (false = inactive)
   *  - status: string     ("Inactive" = inactive)
   */
  private isItemInactive(item: any): boolean {
    if (!item) return false;
    if ('isActive' in item) return item.isActive === false;
    if ('status' in item) return String(item.status).toLowerCase() === 'inactive';
    return false;
  }

  /**
   * Resolves all non-static data sources declared in the template and injects
   * the results into the already-built config via injectDataSourceItems.
   *
   * Call this after buildDynamicForm() and before binding config to the form.
   *
   * @param config   The IDynamicFormConfig returned by buildDynamicForm()
   * @param template The original IFormTemplate (needed for the dataSources map)
   * @param context  Optional live form data for dynamic (context-dependent) params
   */
  resolveDataSources(
    config: IDynamicFormConfig,
    context?: Record<string, any>
  ): Observable<void> {
    // Use the already-parsed dataSources from config (built by buildDynamicForm)
    // rather than template.dataSources where configuration is still a JSON string.
    // REST sources declaring dynamicParams (e.g. mandate-scoped lookups) are skipped
    // until every required param is actually present in context, so no request goes
    // out without the values it depends on (see LoanAmountGridComponent's equivalent guard).
    const nonStaticSources = config.dataSources.filter(ds => {
      if (!ds.isActive) return false;
      if (ds.sourceType === 'static') return ds.sourceKey === 'mandates';
      const dynamicParams = (ds.configuration?.dynamicParams ?? []).map(toDynamicParamMapping);
      if (!dynamicParams.length) return true;
      return this.hasAllDynamicParams(dynamicParams.map(p => p.field), context);
    });

    if (!nonStaticSources.length) return of(undefined);

    const resolvers: Record<string, Observable<any[]>> = {};
    nonStaticSources.forEach(ds => {
      resolvers[ds.sourceKey] = this.dataSourceService.resolve(ds, context).pipe(
        catchError(error => {
          console.warn(`[DynamicFormBuilderService] Failed to resolve data source "${ds.sourceKey}".`, error);
          return of([]);
        })
      );
    });

    return forkJoin(resolvers).pipe(
      map((resolved: Record<string, any[]>) => {
        this.injectDataSourceItems(config, resolved);
      })
    );
  }

  private hasAllDynamicParams(fields: string[], context?: Record<string, any>): boolean {
    return fields.every(key => typeof context?.[key] === 'string' ? context[key].trim().length > 0 : context?.[key] != null);
  }

  /** Deduplicates by valueExpr so multi-join endpoints don't produce repeated dropdown entries. */
  private dedupeByValueExpr(allItems: any[], valueExpr?: string): any[] {
    if (!valueExpr) return allItems;
    const seen = new Set<any>();
    return allItems.filter((i: any) => {
      const key = i[valueExpr];
      if (key == null || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /**
   * Re-resolves the data source behind `targetField` using the live form values and
   * swaps the resulting items into the editor. Nothing is requested until every
   * dynamic param has a value; until then the editor is emptied.
   */
  private reloadTargetItems(
    config: IDynamicFormConfig,
    targetField: string,
    formDataRef: any,
    formGetter: (() => any) | undefined,
    isLatest: () => boolean
  ): void {
    const targetItem = this.findItemByDataField(config.items as any[], targetField);
    const ds = config.dataSources.find(d => d.sourceKey === targetItem?._dataSourceKey);
    if (!targetItem || !ds) return;

    const setItems = (items: any[]) => {
      targetItem._allItems = items;
      targetItem.editorOptions ??= {};
      targetItem.editorOptions.items = items;
      formGetter?.()?.getEditor(targetField)?.option('items', items);
    };

    const fields = (ds.configuration?.dynamicParams ?? []).map(toDynamicParamMapping).map(p => p.field);
    if (!this.hasAllDynamicParams(fields, formDataRef)) {
      setItems([]);
      return;
    }

    this.dataSourceService.resolve(ds, formDataRef).pipe(
      catchError(error => {
        console.warn(`[DynamicFormBuilderService] Failed to reload data source "${ds.sourceKey}".`, error);
        return of([]);
      })
    ).subscribe(items => {
      if (!isLatest()) return;
      const valueExpr: string | undefined = targetItem.editorOptions?.valueExpr;
      setItems(this.dedupeByValueExpr(items, valueExpr).filter((i: any) => !this.isItemInactive(i)));
    });
  }

  /**
   * Post-build helper: populates editorOptions.items on any standard field
   * whose data source is dynamic (graphql / api). The component calls this
   * after fetching the resolved data, keyed by sourceKey.
   *
   * @param resolvedData - Map of sourceKey → item array
   *   e.g. { loans: this.loanData, feeTypes: this.feeTypes }
   */
  injectDataSourceItems(config: IDynamicFormConfig, resolvedData: Record<string, any[]>): void {
    const inject = (items: any[]): void => {
      items.forEach(item => {
        if (item._dataSourceKey && resolvedData[item._dataSourceKey]) {
          const allItems = resolvedData[item._dataSourceKey];
          item.editorOptions ??= {};
          // When the field has a valueExpr (e.g. facilityId), deduplicate by
          // that key so multi-join endpoints don't flood the dropdown with
          // identical display entries. Fields without a valueExpr are unaffected.
          const valueExpr: string | undefined = item.editorOptions.valueExpr;
          const dedupedItems = this.dedupeByValueExpr(allItems, valueExpr);
          // Store the deduped set so showInactiveLoansChanged can restore all
          // items when the checkbox is checked without re-introducing duplicates.
          item._allItems = dedupedItems;
          // Default: show only active items (matching showInactiveLoans = false).
          // showInactiveLoansChanged will swap in the full set when ticked.
          item.editorOptions.items = dedupedItems.filter((i: any) => !this.isItemInactive(i));
          // Attach a built-in item template that colours inactive entries red,
          // matching the loan-amount-grid display convention.
          const displayExprFn: (i: any) => string =
            typeof item.editorOptions.displayExpr === 'function'
              ? item.editorOptions.displayExpr
              : (i: any) => i?.[item.editorOptions.displayExpr ?? 'name'] ?? '';
          item.editorOptions.itemTemplate = this.buildLoanStatusItemTemplate(displayExprFn);
        }
        if (item.items) inject(item.items);
      });
    };
    inject(config.items as any[]);
  }

  /**
   * Builds a DevExtreme-compatible itemTemplate function that renders the item
   * text and colours inactive entries with the --color-inactive CSS variable
   * (falls back to #d32f2f), matching the loan-amount-grid convention.
   */
  private buildLoanStatusItemTemplate(displayFn: (item: any) => string): (itemData: any, _idx: number, el: any) => void {
    return (itemData: any, _idx: number, el: any) => {
      let text = displayFn(itemData);
      if ('isActive' in (itemData ?? {}) && !/\s\((Active|Inactive)\)$/.test(text)) {
        text = `${text} (${this.isItemInactive(itemData) ? 'Inactive' : 'Active'})`;
      }
      const color = this.isItemInactive(itemData) ? 'var(--color-inactive, #d32f2f)' : 'inherit';

      // Clear any existing content in the container element
      while (el.firstChild) {
        el.removeChild(el.firstChild);
      }

      // Safely create and append a span with the appropriate color and text
      const span = document.createElement('span');
      (span.style as any).color = color;
      span.textContent = text;
      el.appendChild(span);
    };
  }

  private evalCondition(condition: string, e: any): boolean {
    // Safe evaluation: only compare against e.value directly
    if (condition === 'value === true') return e.value === true;
    if (condition === 'value === false') return e.value === false;
    return false;
  }

  private parseValidationRules(rules?: IFormValidationRule[] | string): IDynamicValidationRule[] {
    if (!rules) return [];
    // validationRules may arrive as a JSON string from the API — parse it if needed.
    const parsed: IFormValidationRule[] = typeof rules === 'string' ? JSON.parse(rules) : rules;
    return parsed.map(rule => {
      // 'greaterThan' and 'lessThan' are convenience types that compile down to
      // DevExtreme 'custom' rules with a self-contained validationCallback.
      if (rule.type === 'greaterThan') {
        const threshold = rule.greaterThan ?? 0;
        return {
          type: 'custom' as const,
          message: rule.message,
          reevaluate: rule.reevaluate ?? true,
          validationCallback: (e: any) => {
            const isValid = e.value != null && e.value > threshold;
            e.isValid = isValid;
            return isValid;
          }
        };
      }
      if (rule.type === 'lessThan') {
        const threshold = rule.lessThan ?? 0;
        return {
          type: 'custom' as const,
          message: rule.message,
          reevaluate: rule.reevaluate ?? true,
          validationCallback: (e: any) => {
            const isValid = e.value != null && e.value < threshold;
            e.isValid = isValid;
            return isValid;
          }
        };
      }
      if (rule.type === 'custom' && rule.validationCallback === 'futureDate') {
        return {
          type: 'custom' as const,
          message: rule.message,
          reevaluate: rule.reevaluate ?? true,
          validationCallback: (e: any) => {
            const value = e.value instanceof Date ? e.value : new Date(e.value);
            const tomorrow = new Date();
            tomorrow.setHours(0, 0, 0, 0);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const isValid = !Number.isNaN(value.getTime()) && value >= tomorrow;
            e.isValid = isValid;
            return isValid;
          }
        };
      }
      return {
        type: rule.type,
        message: rule.message,
        min: rule.min,
        max: rule.max
      };
    });
  }

  private defaultRequestType(): IDynamicFormField {
    return {
      dataField: 'requestType',
      editorType: 'dxTextBox',
      label: { text: 'Request Type' },
      colSpan: 2,
      cssClass: 'dx-widget',
      index: 0,
      editorOptions: {
        readOnly: true,
        required: true
      },
      validationRules: [
        {
          type: 'required',
          message: 'Request Type is required'
        }
      ],
    }
  }
}